/**
 * Duration conversion.
 *
 * The conversion convention is stated here in one place, exported as data, and
 * displayed in the interface. It is a *convention chosen for this tool*, not a
 * requirement taken from any standard.
 *
 * Convention:
 *   1 year  = 365 days       (no leap-year adjustment)
 *   1 month = 365 / 12 days  = 30.416666... days
 *
 * Consequence: 3 years -> 1095 days; 36 months -> 1095 days. The year and month
 * conversions are therefore internally consistent with each other.
 *
 * An alternative convention (365.25 days/year, 30.4375 days/month) is used by
 * some organisations. For a 3-year shelf life the two conventions differ by
 * about 0.75 days of real-time duration, which becomes a fraction of a day
 * after division by the aging factor. The user is told which one is in use so
 * the difference is visible rather than hidden.
 */

import type { DurationUnit } from '../types';

export const DAYS_PER_YEAR = 365;
export const DAYS_PER_MONTH = 365 / 12;

export const CONVERSION_CONVENTION = {
  daysPerYear: DAYS_PER_YEAR,
  daysPerMonth: DAYS_PER_MONTH,
  summary: '1 year = 365 days; 1 month = 365/12 ≈ 30.4167 days. No leap-year adjustment.',
  alternativeNote:
    'Some organisations use 365.25 days/year. This tool does not, so that a stated 3-year shelf life converts to exactly 1095 days.',
} as const;

export function daysPerUnit(unit: DurationUnit): number {
  switch (unit) {
    case 'days':
      return 1;
    case 'months':
      return DAYS_PER_MONTH;
    case 'years':
      return DAYS_PER_YEAR;
  }
}

/** Convert a real-time duration to days. No rounding is applied. */
export function toDays(value: number, unit: DurationUnit): number {
  return value * daysPerUnit(unit);
}

/** Describe the conversion arithmetic for the derivation panel. */
export function describeConversion(value: number, unit: DurationUnit): string {
  if (unit === 'days') return `${value} days (no conversion applied)`;
  if (unit === 'years') return `${value} years × ${DAYS_PER_YEAR} days/year`;
  return `${value} months × (365 / 12) days/month`;
}

export const UNIT_LABELS: Record<DurationUnit, string> = {
  days: 'days',
  months: 'months',
  years: 'years',
};

/**
 * Add a whole number of days to an ISO date (yyyy-mm-dd) and return ISO date.
 * Uses UTC arithmetic to avoid daylight-saving shifts moving the result by a day.
 */
export function addDaysToISODate(isoDate: string, wholeDays: number): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return undefined;
  const [, y, m, d] = match;
  const start = Date.UTC(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(start)) return undefined;
  if (!Number.isFinite(wholeDays)) return undefined;
  const end = new Date(start + wholeDays * 86_400_000);
  if (Number.isNaN(end.getTime())) return undefined;
  return end.toISOString().slice(0, 10);
}
